/**
 * WEB COPY of SignUpScreen.tsx — picked by the web bundler ahead of the mobile
 * file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Difference from the mobile screen: the ornament is drawn by the shared
 * OrnamentBackground component (placed outside the ScrollView) instead of an
 * inline image inside the hero, so on the desktop frame it spans the whole
 * window (see src/components/OrnamentBackground.web.tsx). Everything else is
 * identical. When SignUpScreen.tsx changes, re-apply the change here.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
  SafeAreaView,
  Animated,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';

// Eye icons for password visibility toggle
import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';

import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
import OrnamentBackground from '../../components/OrnamentBackground';
import { analytics, AnalyticsEvent } from '../../services/analytics';
const logger = createLogger('SignUp');

type RootStackParamList = {
  SignIn: undefined;
  UserOnboarding: undefined;
  Login: undefined;
  EmailVerificationNotice: { email: string; phoneNumber?: string };
  SignUp: {
    isInvitedUser?: boolean;
    invitationToken?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    referralCode?: string;
  };
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type SignUpRouteProp = RouteProp<RootStackParamList, 'SignUp'>;

// Helper function to format phone number (defined outside component to avoid hoisting issues)
const formatPhoneNumberHelper = (text: string) => {
  // Remove all non-numeric characters
  const cleaned = text.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
};

export const SignUpScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<SignUpRouteProp>();

  // Get invitation params if available
  const invitationParams = route.params || {};
  const isInvitedUser = invitationParams.isInvitedUser || false;
  const invitationToken = invitationParams.invitationToken;
  const invitationFirstName = invitationParams.firstName || '';
  const invitationLastName = invitationParams.lastName || '';

  const [email, setEmail] = useState(invitationParams.email || '');
  const [phoneNumber, setPhoneNumber] = useState(
    invitationParams.phoneNumber ? formatPhoneNumberHelper(invitationParams.phoneNumber) : ''
  );
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState(invitationParams.referralCode || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const slideAnim = useRef(new Animated.Value(-50)).current;
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    legal: '',
  });

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: showPasswordError ? 0 : -50,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [showPasswordError]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string) => {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it's 10 digits (US format) or 11 digits (with country code)
    return cleaned.length === 10 || cleaned.length === 11;
  };

  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const validatePassword = (password: string) => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[^a-zA-Z0-9]/.test(password);

    return hasMinLength && hasUpperCase && hasNumber && hasSpecialChar;
  };

  const handleSignUp = async () => {
    if (isLoading) return; // Prevent double submission
    analytics.track(AnalyticsEvent.SignupStarted, { invited: Boolean(isInvitedUser) });

    const newErrors = {
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      legal: '',
    };

    if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!validatePassword(password)) {
      newErrors.password = 'Password must be 8+ chars with 1 uppercase, 1 number, and 1 special character';
      setShowPasswordError(true);
    } else {
      setShowPasswordError(false);
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!agreedToTerms || !agreedToPrivacy) {
      newErrors.legal = 'You must agree to the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== '');

    if (!hasErrors) {
      setIsLoading(true);
      try {
        // Remove formatting from phone number for backend
        const cleanedPhone = phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
        // Normalize email so it matches the login form's lowercase lookup
        // (LoginScreen sends email.toLowerCase().trim()).
        const normalizedEmail = email.trim().toLowerCase();

        // Call backend registration API
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: normalizedEmail,
            ...(cleanedPhone && { phoneNumber: cleanedPhone }),
            password,
            firstName: invitationFirstName, // Pre-filled from invitation or empty
            lastName: invitationLastName,   // Pre-filled from invitation or empty
            isInvitedUser: isInvitedUser,
            invitationToken: invitationToken,
            ...(referralCode.trim() && { referralCode: referralCode.trim().toUpperCase() }),
          }),
        });

        const result = await response.json();

        if (response.ok) {
          // Store email, phone for verification flow
          await AsyncStorage.setItem('@signup_email', normalizedEmail);
          await AsyncStorage.setItem('@signup_phone', cleanedPhone);

          // Store invitation token for acceptance after completing signup flow
          if (invitationToken) {
            await AsyncStorage.setItem('@invitation_token', invitationToken);
          }

          // Don't store auth token during signup - only store after login
          // This ensures users are taken to Login screen when they reopen the app

          analytics.track(AnalyticsEvent.SignupSubmitted);
          analytics.track(AnalyticsEvent.EmailVerificationSent);

          // Navigate to email verification notice screen
          navigation.replace('EmailVerificationNotice', { email: normalizedEmail, phoneNumber: cleanedPhone });
        } else {
          // Handle registration error
          analytics.track(AnalyticsEvent.SignupFailed, { reason: result?.message || 'unknown' });
          logger.error('Registration failed:', result);
          const errorMessage = result.message || 'Registration failed. Please try again.';
          setErrors({
            ...newErrors,
            email: result.message?.includes('email') ? errorMessage : '',
            phoneNumber: result.message?.includes('phone') ? errorMessage : '',
            password: result.message?.includes('password') ? errorMessage : '',
            confirmPassword: !result.message?.includes('email') && !result.message?.includes('phone') && !result.message?.includes('password') ? errorMessage : ''
          });
        }
      } catch (error) {
        logger.error('Registration error:', error);
        setErrors({
          ...newErrors,
          confirmPassword: 'Network error. Please check your connection and try again.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
      <ScrollView
        style={styles.outerScrollView}
        contentContainerStyle={styles.outerScrollContent}
        keyboardShouldPersistTaps="handled"
        bounces={false}
      >
      <View style={styles.topSection}>
        <Image
          source={require('../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          {isInvitedUser ? 'Accept Invitation' : 'Create Account'}
        </Text>
        <Text style={styles.subtitle}>
          {isInvitedUser
            ? 'Complete your account setup to accept the invitation and access shared information'
            : 'Ensure that your loved ones will have access to the critical information they need when the time comes'}
        </Text>
      </View>

      <View style={styles.mainContainer}>
        {showPasswordError && (
          <Animated.View 
            style={[
              styles.errorBox,
              {
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <View style={styles.errorContent}>
              <Image
                source={require('../../assets/icons/warning.png')}
                style={styles.warningIcon}
              />
              <Text style={styles.errorMessage}>
                Your password does not meet the minimum requirements
              </Text>
              <TouchableOpacity
                onPress={() => setShowPasswordError(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        )}

        <View style={[styles.inputContainer]}>
          <View style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={[styles.input, isInvitedUser && invitationParams.email && styles.inputDisabled]}
                value={email}
                onChangeText={setEmail}
                placeholder="Enter your email"
                placeholderTextColor="#A9A9A9"
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                editable={!(isInvitedUser && invitationParams.email)}
              />
              {isInvitedUser && invitationParams.email && (
                <Text style={styles.helperText}>Email is pre-filled from your invitation</Text>
              )}
              {errors.email ? (
                <Text style={styles.errorText}>{errors.email}</Text>
              ) : null}
            </View>

            {/* Letter-first signups collect email + password only — phone
                comes later at the Level-2 profile step. Invited users keep
                the field since their flow may pre-fill it. */}
            {isInvitedUser && (
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Phone Number (Optional)</Text>
                <TextInput
                  style={styles.input}
                  value={phoneNumber}
                  onChangeText={(text) => setPhoneNumber(formatPhoneNumber(text))}
                  placeholder="(555) 123-4567"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                  maxLength={14}
                />
                {errors.phoneNumber ? (
                  <Text style={styles.errorText}>{errors.phoneNumber}</Text>
                ) : null}
              </View>
            )}

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Password</Text>
              <View style={styles.passwordInputContainer}>
                <TextInput
                  style={styles.passwordInput}
                  value={password}
                  onChangeText={setPassword}
                  placeholder="Create a password"
                  placeholderTextColor="#A9A9A9"
                  secureTextEntry={!showPassword}
                  textContentType="newPassword"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Image
                    source={showPassword ? eyeIcon : eyeClosedIcon}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              <Text style={styles.requirementsText}>
                Min 8 chars • 1 uppercase • 1 number • 1 special character
              </Text>
              {errors.password ? (
                <Text style={styles.errorText}>{errors.password}</Text>
              ) : null}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Confirm Password</Text>
              <View style={styles.passwordInputContainer}>
                <TextInput
                  style={styles.passwordInput}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder="Confirm your password"
                  placeholderTextColor="#A9A9A9"
                  secureTextEntry={!showConfirmPassword}
                  textContentType="newPassword"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={styles.eyeButton}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Image
                    source={showConfirmPassword ? eyeIcon : eyeClosedIcon}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              {errors.confirmPassword ? (
                <Text style={styles.errorText}>{errors.confirmPassword}</Text>
              ) : null}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Referral Code (Optional)</Text>
              <TextInput
                style={styles.input}
                value={referralCode}
                onChangeText={setReferralCode}
                placeholder="Referral Code (optional)"
                placeholderTextColor="#A9A9A9"
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={20}
              />
            </View>

            <View style={styles.legalContainer}>
              <TouchableOpacity
                style={styles.checkboxContainer}
                onPress={() => setAgreedToTerms(!agreedToTerms)}
              >
                <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                  {agreedToTerms && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>
                  I agree to the{' '}
                  <Text
                    style={styles.linkText}
                    onPress={(e) => {
                      e.stopPropagation();
                      navigation.navigate('TermsOfService' as any);
                    }}
                  >
                    Terms of Service
                  </Text>
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxContainer}
                onPress={() => setAgreedToPrivacy(!agreedToPrivacy)}
              >
                <View style={[styles.checkbox, agreedToPrivacy && styles.checkboxChecked]}>
                  {agreedToPrivacy && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>
                  I agree to the{' '}
                  <Text
                    style={styles.linkText}
                    onPress={(e) => {
                      e.stopPropagation();
                      navigation.navigate('PrivacyPolicy' as any);
                    }}
                  >
                    Privacy Policy
                  </Text>
                </Text>
              </TouchableOpacity>

              {errors.legal ? (
                <Text style={styles.errorText}>{errors.legal}</Text>
              ) : null}
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity 
                style={[styles.submitButton, isLoading && styles.submitButtonDisabled]} 
                onPress={handleSignUp}
                disabled={isLoading}
              >
                <Text style={styles.submitButtonText}>
                  {isLoading ? 'Creating Account...' : 'Sign Up'}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.signInContainer}>
              <Text style={styles.signInText}>Already have an account?</Text>
              <TouchableOpacity onPress={() => navigation.navigate('Login')}>
                <Text style={styles.signInLink}>Sign In</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.bottomPadding} />
          </View>
        </View>
      </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  keyboardAvoid: {
    flex: 1,
  },
  outerScrollView: {
    flex: 1,
  },
  outerScrollContent: {
    flexGrow: 1,
    paddingBottom: Platform.OS === 'android' ? 40 : 0,
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: 0,
  },
  backgroundKey: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(50),
    paddingHorizontal: ms(23),
  },
  mainContainer: {
    flex: 1,
    width: '100%',
    position: 'relative',
  },
  errorBox: {
    position: 'absolute',
    top: ms(-60),
    left: 0,
    right: 0,
    backgroundColor: '#E85C5C',
    paddingVertical: ms(15),
    paddingHorizontal: ms(20),
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    zIndex: 0,
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    position: 'relative',
    zIndex: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 5,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  passwordInput: {
    flex: 1,
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  eyeButton: {
    padding: ms(15),
  },
  eyeIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#6B7280',
  },
  inputDisabled: {
    backgroundColor: '#E8E8E8',
    color: '#666666',
  },
  helperText: {
    fontSize: ms(12, 0.3),
    color: '#29BFC0',
    marginTop: ms(4),
    marginLeft: ms(4),
    fontStyle: 'italic',
  },
  errorText: {
    color: '#FF6B6B',
    fontSize: ms(14, 0.3),
    marginTop: ms(5),
  },
  legalContainer: {
    marginTop: ms(15),
    marginBottom: ms(10),
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(6),
    borderWidth: 2,
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(10),
  },
  checkboxChecked: {
    backgroundColor: '#29BFC0',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    flex: 1,
  },
  linkText: {
    color: '#29BFC0',
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  submitButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  signInContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(10),
    marginBottom: ms(20),
  },
  signInText: {
    color: '#62656b',
    fontSize: ms(16, 0.3),
  },
  signInLink: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    marginLeft: ms(5),
  },
  bottomPadding: {
    height: s(40),
  },
  requirementsText: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(8),
    marginLeft: ms(4),
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  warningIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#FFFFFF',
  },
  errorMessage: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    marginHorizontal: ms(10),
  },
  closeButton: {
    padding: ms(5),
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
});

export default SignUpScreen; 